import { deepmerge } from "@mui/utils";
import dayjs, { Dayjs } from "dayjs";
import {
  getBitcoinPrice,
  getBtcActiveAddressesWithPrice,
  getBtcAddressCountChangeWithPrice,
  getBTCAhr999AdjustedCorrelation,
  getBTCAhr999Correlation,
  getBtcLongShortRatioCorrelation,
  getBtcLongShortTermMvrvCorrelation,
  getBtcMovingAveragesWithPrice,
  getBtcMvrvCorrelation,
  getBtcNuplCorrelation,
  getBTCPriceAndFearGreed,
  getBtcPsipCorrelation,
  getBtcPuellMultipleCorrelation,
  getBtcRealizedCapWithPrice,
  getBtcRelativeUnrealizedProfitWithPrice,
  getBtcSoprWithPrice,
  getTrendIndicators,
  getBtcMacdData,
} from "./cryptoData/bitcoin";
import { getEthPrice } from "./cryptoData/ethereum";
import { getSolPrice } from "./cryptoData/solana";
import { lineChartOption, lineStackBarChartOption, setLineAreaGradient } from "./echartOption";

const DEFAULT_START_DATE = "2020-01-01";

interface MetricData {
  data?: any[];
  locked?: boolean;
}

interface Metric {
  id: number;
  name: string;
  url: string;
  i18nKey?: string;
  isPro?: boolean;
  loadData: (start?: Dayjs, end?: Dayjs) => Promise<MetricData>;
  chartOption: (data: any[], t: any) => any;
  getMarketCondition?: (data: any[]) => { value: number; condition: string } | null;
}

function getMarketCondition(data: any[], index: number, under: number, over: number) {
  if (!data || data.length === 0) return null;
  const indicators = data[index];
  if (!indicators || indicators.length === 0) return null;

  const indicator = indicators[indicators.length - 1];
  if (indicator === undefined) return null;

  if (indicator < under) {
    return { value: indicator, condition: "undervalued" };
  } else if (indicator < over) {
    return { value: indicator, condition: "neutral" };
  } else {
    return { value: indicator, condition: "overvalued" };
  }
}

export const FearAndGreed: Metric = {
  id: 1,
  name: "Fear And Greed Index",
  url: "fear-and-greed-index",
  i18nKey: "fearAndGreed",
  isPro: true,
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBTCPriceAndFearGreed(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    if (data.locked) return { locked: true };
    const dates = data.dates?.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, data.prices, data.fearGreedValues, data.correlations] };
  },
  chartOption(data, t) {
    const opt = lineChartOption(
      { title: "", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: t("fearAndGreed.title"), data: data[2] },
        { title: t("correlation"), data: data[3] },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
    return opt;
  },
  getMarketCondition(data: any[]) {
    return getMarketCondition(data, 2, 40, 60);
  },
};

export const Ahr999: Metric = {
  id: 2,
  name: "Ahr999 Index",
  url: "ahr999-index-original",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBTCAhr999Correlation(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    const dates = data.dates.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, data.prices, data.ahr999, data.correlations, data.avg200] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "date", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1], yAxisIndex: 0 },
        { title: "Ahr999", data: data[2], yAxisIndex: 1 },
        { title: t("ahr999Adjusted.200DayAverage"), data: data[3], yAxisIndex: 2 },
        { title: t("correlation"), data: data[4], yAxisIndex: 3 },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
  getMarketCondition(data: any[]) {
    return getMarketCondition(data, 2, 0.45, 1.2);
  },
};

export const Ahr999Adjusted: Metric = {
  id: 3,
  name: "Ahr999 Index",
  url: "ahr999-index",
  i18nKey: "ahr999Adjusted",
  isPro: true,
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const btcAhr999Adjusted = await getBTCAhr999AdjustedCorrelation(
      start.format("YYYY-MM-DD"),
      end.format("YYYY-MM-DD")
    );
    if (btcAhr999Adjusted.locked) return { locked: true };
    const dates = btcAhr999Adjusted.dates?.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return {
      data: [
        dates,
        btcAhr999Adjusted.prices,
        btcAhr999Adjusted.ahr999,
        btcAhr999Adjusted.correlations,
        btcAhr999Adjusted.avg200,
      ],
    };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "date", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: "Ahr999", data: data[2] },
        { title: t("correlation"), data: data[3] },
        { title: t("ahr999Adjusted.200DayAverage"), data: data[4] },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
  getMarketCondition(data: any[]) {
    return getMarketCondition(data, 2, 0.45, 1.2);
  },
};

export const NUPL: Metric = {
  id: 4,
  name: "Net Unrealized Profit/Loss",
  url: "nupl",
  i18nKey: "nupl",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const btcNupl = await getBtcNuplCorrelation(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    const dates = btcNupl.dates.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, btcNupl.prices, btcNupl.nupl, btcNupl.correlations] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "date", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: "NUPL", data: data[2] },
        { title: t("correlation"), data: data[3] },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
  getMarketCondition(data: any[]) {
    return getMarketCondition(data, 2, 0.43, 0.51);
  },
};

export const MVRV: Metric = {
  id: 5,
  name: "MVRV Ratio",
  url: "mvrv-ratio",
  i18nKey: "mvrv",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const btcMvrv = await getBtcMvrvCorrelation(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    if (btcMvrv.locked) return { locked: true };
    const dates = btcMvrv.dates?.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, btcMvrv.prices, btcMvrv.mvrv, btcMvrv.correlations] };
  },
  isPro: true,
  chartOption(data, t) {
    return lineChartOption(
      { title: "date", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: "MVRV", data: data[2] },
        { title: t("correlation"), data: data[3] },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
  getMarketCondition(data: any[]) {
    return getMarketCondition(data, 2, 1, 2);
  },
};

export const ShortTermMVRV: Metric = {
  id: 6,
  name: "Short Term MVRV Ratio",
  url: "short-term-mvrv-ratio",
  i18nKey: "shortTermMvrv",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const btcLongShortTermMvrv = await getBtcLongShortTermMvrvCorrelation(
      start.format("YYYY-MM-DD"),
      end.format("YYYY-MM-DD")
    );
    const dates = btcLongShortTermMvrv.dates.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return {
      data: [dates, btcLongShortTermMvrv.prices, btcLongShortTermMvrv.shortTermMvrv, btcLongShortTermMvrv.correlations],
    };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "date", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: t("shortTermMvrv.title"), data: data[2] },
        { title: t("correlation"), data: data[3] },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
};

export const PercentOfSupplyInProfit: Metric = {
  id: 7,
  name: "Percent of Supply in Profit",
  url: "percent-of-supply-in-profit",
  i18nKey: "percentOfSupplyInProfit",
  isPro: true,
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const btcPsip = await getBtcPsipCorrelation(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    if (btcPsip.locked) return { locked: true };
    const dates = btcPsip.dates?.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, btcPsip.prices, btcPsip.psip, btcPsip.correlations] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "date", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: "PSIP", data: data[2] },
        { title: t("correlation"), data: data[3] },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
  getMarketCondition(data: any[]) {
    return getMarketCondition(data, 2, 66.5, 82.5);
  },
};

export const PuellMultiple: Metric = {
  id: 8,
  name: "Puell Multiple",
  url: "puell-multiple",
  i18nKey: "puellMultiple",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const btcPuellMultiple = await getBtcPuellMultipleCorrelation(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    const dates = btcPuellMultiple.dates.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, btcPuellMultiple.prices, btcPuellMultiple.puellMultiple, btcPuellMultiple.correlations] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "date", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: t("puellMultiple.title"), data: data[2] },
        { title: t("correlation"), data: data[3] },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
  getMarketCondition(data: any[]) {
    return getMarketCondition(data, 2, 0.5, 1.1);
  },
};

export const GlobalLongShortRatio: Metric = {
  id: 9,
  name: "Long/Short Ratio",
  url: "long-short-ratio",
  i18nKey: "longShortRatio",
  isPro: true,
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const btcLongShortRatio = await getBtcLongShortRatioCorrelation(
      start.format("YYYY-MM-DD"),
      end.format("YYYY-MM-DD")
    );
    if (btcLongShortRatio.locked) return { locked: true };
    const dates = btcLongShortRatio.dates?.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return {
      data: [
        dates,
        btcLongShortRatio.prices,
        btcLongShortRatio.lsRatio,
        btcLongShortRatio.correlations,
        btcLongShortRatio.longValue,
        btcLongShortRatio.shortValue,
      ],
    };
  },
  chartOption(data, t) {
    return lineStackBarChartOption(
      { title: "date", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: t("longShortRatio.LSRatio"), data: data[2] },
        { title: t("correlation"), data: data[3] },
      ],
      [
        { title: t("longShortRatio.longValue"), data: data[4] },
        { title: t("longShortRatio.shortValue"), data: data[5] },
      ]
    );
  },
  getMarketCondition(data: any[]) {
    return getMarketCondition(data, 2, 0.9, 1.05);
  },
};

export const BTCPrice: Metric = {
  id: 10,
  name: "Bitcoin Price",
  url: "bitcoin-price",
  i18nKey: "bitcoinPrice",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBitcoinPrice(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    const dates = data.map((item) => dayjs(item.date).format("YYYY-MM-DD"));
    const prices = data.map((item) => item.price);
    return { data: [dates, prices] };
  },
  chartOption(data, t) {
    let opt = lineChartOption(
      { title: "", data: data[0] },
      [{ title: t("bitcoinPrice.title"), data: data[1] }],
      undefined,
      { 0: { useLogScale: true } }
    );
    opt = deepmerge(opt, {
      grid: {
        left: "60px",
        right: "50px",
      },
    });
    setLineAreaGradient(opt);
    return opt;
  },
};

export const ETHPrice: Metric = {
  id: 11,
  name: "Ethereum Price",
  url: "ethereum-price",
  i18nKey: "ethPrice",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getEthPrice(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    const dates = data.map((item) => dayjs(item.date).format("YYYY-MM-DD"));
    const prices = data.map((item) => item.price);
    return { data: [dates, prices] };
  },
  chartOption(data, t) {
    let opt = lineChartOption(
      { title: "", data: data[0] },
      [{ title: t("ethPrice.title"), data: data[1] }],
      undefined,
      { 0: { useLogScale: true } }
    );
    opt = deepmerge(opt, {
      grid: {
        left: "60px",
        right: "50px",
      },
    });
    setLineAreaGradient(opt);
    return opt;
  },
};

export const SOLPrice: Metric = {
  id: 12,
  name: "Solana Price",
  url: "solana-price",
  i18nKey: "solPrice",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getSolPrice(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    const dates = data.map((item) => dayjs(item.date).format("YYYY-MM-DD"));
    const prices = data.map((item) => item.price);
    return { data: [dates, prices] };
  },
  chartOption(data, t) {
    let opt = lineChartOption(
      { title: "", data: data[0] },
      [{ title: t("solPrice.title"), data: data[1] }],
      undefined,
      { 0: { useLogScale: true } }
    );
    opt = deepmerge(opt, {
      grid: {
        left: "60px",
        right: "50px",
      },
    });
    setLineAreaGradient(opt);
    return opt;
  },
};

export const BtcTrend: Metric = {
  id: 13,
  name: "btc trend name",
  url: "btc-trend",
  i18nKey: "btcTrend",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getTrendIndicators("btc", start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    return { data };
  },
  chartOption(data, t) {
    const dates = data.map((item) => dayjs(item.date).format("YYYY-MM-DD"));
    const prices = data.map((item) => item.value);
    let opt = lineChartOption({ title: "", data: dates }, [{ title: "trend", data: prices }]);
    return opt;
  },
};

export const EthTrend: Metric = {
  id: 14,
  name: "ETH Trend",
  url: "eth-trend",
  i18nKey: "ethTrend",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getTrendIndicators("eth", start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    return { data };
  },
  chartOption(data, t) {
    const dates = data.map((item) => dayjs(item.date).format("YYYY-MM-DD"));
    const prices = data.map((item) => item.value);
    let opt = lineChartOption({ title: "", data: dates }, [{ title: "trend", data: prices }]);
    return opt;
  },
};

export const SolTrend: Metric = {
  id: 15,
  name: "SOL Trend",
  url: "sol-trend",
  i18nKey: "solTrend",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getTrendIndicators("sol", start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    return { data };
  },
  chartOption(data, t) {
    const dates = data.map((item) => dayjs(item.date).format("YYYY-MM-DD"));
    const prices = data.map((item) => item.value);
    let opt = lineChartOption({ title: "", data: dates }, [{ title: "trend", data: prices }]);
    return opt;
  },
};

export const RelativeUnrealizedProfit: Metric = {
  id: 16,
  name: "Relative Unrealized Profit",
  url: "relative-unrealized-profit",
  i18nKey: "relativeUnrealizedProfit",
  isPro: true,
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBtcRelativeUnrealizedProfitWithPrice(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    if (data.locked) return { locked: true };
    const dates = data.dates?.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, data.prices, data.relativeUnrealizedProfit, data.correlations] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1], yAxisIndex: 0 },
        { title: t("relativeUnrealizedProfit.title"), data: data[2], yAxisIndex: 1 },
        { title: t("correlation"), data: data[3], yAxisIndex: 2 },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
};

export const RealizedCap: Metric = {
  id: 17,
  name: "Realized Cap",
  url: "realized-cap",
  i18nKey: "realizedCap",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBtcRealizedCapWithPrice(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    const dates = data.dates.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, data.prices, data.realizedCap, data.marketCap] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: t("realizedCap.title"), data: data[2] },
        { title: t("realizedCap.marketCap"), data: data[3], yAxisIndex: 1 },
      ],
      undefined,
      { 0: { useLogScale: true }, 1: { useLogScale: true } }
    );
  },
};

export const SOPR: Metric = {
  id: 18,
  name: "SOPR",
  url: "sopr",
  i18nKey: "sopr",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBtcSoprWithPrice(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    const dates = data.dates.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, data.prices, data.sopr, data.correlations] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: t("sopr.title"), data: data[2] },
        { title: t("correlation"), data: data[3] },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
};

export const ActiveAddresses: Metric = {
  id: 19,
  name: "Active Addresses",
  url: "active-addresses",
  i18nKey: "activeAddresses",
  isPro: true,
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBtcActiveAddressesWithPrice(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    if (data.locked) return { locked: true };
    const dates = data.dates?.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, data.prices, data.activeAddresses, data.activeAddresses7dMA] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1], yAxisIndex: 0 },
        { title: t("activeAddresses.title"), data: data[2], yAxisIndex: 1 },
        { title: t("activeAddresses.7DayMA"), data: data[3], yAxisIndex: 1 },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
};

export const AddressCountChange: Metric = {
  id: 20,
  name: "Address Count Change",
  url: "address-count-change",
  i18nKey: "addressCountChange",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBtcAddressCountChangeWithPrice(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    const dates = data.dates.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, data.prices, data.addressCountChange, data.addressCountChange30dMA] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1], yAxisIndex: 0 },
        { title: t("addressCountChange.title"), data: data[2], yAxisIndex: 1 },
        { title: t("addressCountChange.30DayMA"), data: data[3], yAxisIndex: 1 },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
};

export const PiCycleTop: Metric = {
  id: 21,
  name: "Pi Cycle Top",
  url: "pi-cycle-top",
  i18nKey: "piCycleTop",
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBtcMovingAveragesWithPrice(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"), false);
    const dates = data.dates?.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, data.prices, data.ma111, data.ma350_2] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1], yAxisIndex: 0 },
        { title: t("piCycleTop.111DayMA"), data: data[2], yAxisIndex: 0 },
        { title: t("piCycleTop.350DayMA2"), data: data[3], yAxisIndex: 0 },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
};

export const MovingAverages: Metric = {
  id: 22,
  name: "Moving Averages",
  url: "moving-averages",
  i18nKey: "movingAverages",
  isPro: true,
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBtcMovingAveragesWithPrice(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    if (data.locked) return { locked: true };
    const dates = data.dates?.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, data.prices, data.ma50, data.ma200] };
  },
  chartOption(data, t) {
    return lineChartOption(
      { title: "", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1], yAxisIndex: 0 },
        { title: t("movingAverages.50DayMA"), data: data[2], yAxisIndex: 0 },
        { title: t("movingAverages.200DayMA"), data: data[3], yAxisIndex: 0 },
      ],
      undefined,
      { 0: { useLogScale: true } }
    );
  },
};

export const MACD: Metric = {
  id: 23,
  name: "MACD",
  url: "macd",
  i18nKey: "macd",
  isPro: true,
  async loadData(start = dayjs(DEFAULT_START_DATE), end = dayjs()) {
    const data = await getBtcMacdData(start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD"));
    if (data.locked) return { locked: true };
    const dates = data.dates?.map((date: Date) => dayjs(date).format("YYYY-MM-DD"));
    return { data: [dates, data.prices, data.macd, data.signal, data.histogramNegative, data.histogramPositive] };
  },
  chartOption(data, t) {
    return lineStackBarChartOption(
      { title: "", data: data[0] },
      [
        { title: t("bitcoinPrice.title"), data: data[1] },
        { title: t("macd.title"), data: data[2] },
        { title: t("macd.signalLine"), data: data[3], yAxisIndex: 1 },
      ],
      [
        { title: t("macd.histogramNegative"), data: data[4], yAxisIndex: 1 },
        { title: t("macd.histogramPositive"), data: data[5], yAxisIndex: 1 },
      ]
    );
  },
};

export const BTC_LONG_TERM_METRICS = {
  FearAndGreed,
  Ahr999Adjusted,
  NUPL,
  MVRV,
  ShortTermMVRV,
  PercentOfSupplyInProfit,
  PuellMultiple,
  GlobalLongShortRatio,
  BTCPrice,
  RealizedCap,
  SOPR,
  RelativeUnrealizedProfit,
  PiCycleTop,
  MovingAverages,
  ActiveAddresses,
  AddressCountChange,
  MACD,
};

export const PRIVATE_METRICS = [BtcTrend, EthTrend, SolTrend];

export const OTHER_METRICS = [ETHPrice, SOLPrice];

export function getMetricById(id: number): Metric | undefined {
  const allMetrics: Metric[] = [
    ...(Object.values(BTC_LONG_TERM_METRICS) as Metric[]),
    ...PRIVATE_METRICS,
    ...OTHER_METRICS,
  ];

  return allMetrics.find((metric: Metric) => metric.id === id);
}

export function getMetricByUrl(url: string): Metric | undefined {
  const allMetrics: Metric[] = [
    ...(Object.values(BTC_LONG_TERM_METRICS) as Metric[]),
    ...PRIVATE_METRICS,
    ...OTHER_METRICS,
  ];

  return allMetrics.find((metric: Metric) => metric.url === url);
}
